/**
 * HorizontalCard component.
 */
import Card from '@shared/types/props/card';
import ChannelUtils from '@shared/utils/channels';
import Container from '../../atoms/container/horizontalCardContainer';
import Content from '@shared/types/enums/content';
import Image from '../../atoms/images/horizontalCardImage';
import ImageUtils from '@shared/utils/image';
import Info from './horizontalCardInfo';
import Mask from '../../atoms/mask/horizontalCardMask';
import MainContext from '@shared/components/utils/context/mainContext';
import ParentalUtils from '@shared/utils/parental';
import PhotoDefault from '@shared/assets/images/png/photoDefault.png';
import useList from '@shared/components/utils/hooks/useList';
import { magicRemoteSelector } from '@shared/store/slices/magicRemote.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useContext, useMemo, useRef, useState } from 'react';

/**
 * Number of items to display in the horizontal rail.
 */
const ITEMS_TO_DISPLAY = 6;

/**
 * Horizontal card component. Represents a single horizontal card in the horizontal rail.
 * Handles lazy loading, focus states, and asset updates on hover.
 *
 * @param {Props} props - Component props including item, index, and onClick handler
 * @returns {JSX.Element | null} Horizontal card element or null if not in view
 */
export default function HorizontalCard(parentProps: Card) {
  // Card hook
  const { display, props } = useHorizontalCard({
    ...parentProps,
  });

  // Render
  return (
    <>
      {display.card && (
        <Container {...props.container} id='horizontalCardContainer'>
          {display.image && (
            <>
              <Image {...props.image} />
              {display.mask ? <Mask /> : null}
              {display.info ? <Info {...props.info} /> : null}
              {parentProps.children}
            </>
          )}
        </Container>
      )}
    </>
  );
}

/**
 * Hook for the horizontal card component.
 */
function useHorizontalCard({
  index,
  item,
  progressBar,
  showInfo = true,
  showMask = true,
  showTitle = true,
  onClick,
  releaseAction,
}: Card) {
  // Selectors
  const { distanceToIndex, focused, listIndex } = useList(index);
  const { hidePreviousItem, setAsset } = useContext(MainContext);
  const { enabled } = useAppSelector(magicRemoteSelector);

  // States
  const [hovered, setHovered] = useState(false);
  const previousRetriesError = useRef(0);

  // Data
  const parentalCheck =
    !ParentalUtils.isAdultRestricted(item) || item.type === Content.ADDON;
  const upgradablePlan =
    distanceToIndex < ITEMS_TO_DISPLAY &&
    distanceToIndex > -ITEMS_TO_DISPLAY &&
    item?.channel
      ? ChannelUtils.getUpgradablePlan(item.channel, false, item.type)
      : null;

  // Event handlers
  const onImageError = (
    event: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const imgElement = event.target as HTMLImageElement;
    if (previousRetriesError.current === 0) {
      previousRetriesError.current += 1;

      imgElement.src = ImageUtils.getImage(item.channel?.card || '', 'photo');
    } else {
      imgElement.src = PhotoDefault;
    }
  };

  // Memoized data
  const { display, props } = useMemo(() => {
    return {
      display: {
        card: distanceToIndex < ITEMS_TO_DISPLAY,
        image: distanceToIndex > (hidePreviousItem ? -1 : -ITEMS_TO_DISPLAY),
        mask: showMask && showTitle,
        info: showInfo,
      },
      props: {
        container: {
          onClick: onClick || releaseAction,
          onMouseEnter: () => {
            setHovered(true);
            setAsset(item);
          },
          onMouseLeave: () => setHovered(false),
        },
        image: {
          src: parentalCheck ? item.photo : '',
          onError: onImageError,
        },
        info: {
          focused: (!enabled && focused && index === listIndex) || hovered,
          item,
          plan: upgradablePlan,
          progressBar,
          showTitle,
        },
      },
    };
  }, [
    distanceToIndex,
    focused,
    listIndex,
    setHovered,
    setAsset,
    item,
    onClick,
    releaseAction,
    showMask,
    showInfo,
    showTitle,
    progressBar,
  ]);

  // Return values
  return { display, props };
}
